"use client";

import { useRef, useCallback } from "react";

/**
 * Attach to a container div via { ...swipeHandlers }.
 * Calls onPrev / onNext when the user swipes right / left by at least 40px.
 */
export function useSwipePage({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}) {
  const startX  = useRef<number | null>(null);
  const startY  = useRef<number | null>(null);
  const locked  = useRef<"h" | "v" | null>(null); // axis lock after 10px

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    locked.current = null;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (startX.current === null || startY.current === null) return;
    const dx = e.touches[0].clientX - startX.current;
    const dy = e.touches[0].clientY - startY.current;

    // Lock axis once one dimension exceeds 10px
    if (!locked.current) {
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        locked.current = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
      }
    }
    // Only prevent vertical scroll when we're sure this is a horizontal swipe
    if (locked.current === "h") e.preventDefault();
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (startX.current === null || locked.current !== "h") {
      startX.current = null;
      startY.current = null;
      locked.current = null;
      return;
    }
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    startY.current = null;
    locked.current = null;

    if (dx < -40) onNext(); // swipe left  → next page
    if (dx >  40) onPrev(); // swipe right → prev page
  }, [onNext, onPrev]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    // CSS touch-action: restrict to y-scroll; JS preventDefault overrides for h swipes
    style: { touchAction: "pan-y" } as React.CSSProperties,
  };
}
